/**
 * What a sidebar menu shows of its rows, by the user's marks on their pages:
 * hidden (kept out of the menus) and favorite (hearted). A page is never both.
 * Each menu has two filters, chosen from its header's eye and heart:
 *
 * - Hidden: `unhidden` (the default) leaves hidden rows out, `all` shows them
 *   dimmed, `hidden` shows only them.
 * - Favorites: `all` (the default), `favorites` only, or `non-favorites` only.
 */
export type HiddenFilter = 'all' | 'unhidden' | 'hidden'
export type FavoriteFilter = 'all' | 'favorites' | 'non-favorites'

export const DEFAULT_HIDDEN_FILTER: HiddenFilter = 'unhidden'
export const DEFAULT_FAVORITE_FILTER: FavoriteFilter = 'all'

export const HIDDEN_FILTER_OPTIONS: readonly { value: HiddenFilter; label: string }[] = [
  { value: 'all', label: 'Show all' },
  { value: 'unhidden', label: 'Show unhidden' },
  { value: 'hidden', label: 'Show hidden' },
]

export const FAVORITE_FILTER_OPTIONS: readonly { value: FavoriteFilter; label: string }[] = [
  { value: 'all', label: 'Show all' },
  { value: 'favorites', label: 'Show favorites' },
  { value: 'non-favorites', label: 'Show non-favorites' },
]

/** A click on the header's eye: between the default and showing everything. */
export function nextHiddenFilter(filter: HiddenFilter): HiddenFilter {
  return filter === 'unhidden' ? 'all' : 'unhidden'
}

/** A click on the header's heart: between everything and favorites only. */
export function nextFavoriteFilter(filter: FavoriteFilter): FavoriteFilter {
  return filter === 'all' ? 'favorites' : 'all'
}

export interface MenuFilters {
  hidden: ReadonlySet<string>
  favorites: ReadonlySet<string>
  hiddenFilter: HiddenFilter
  favoriteFilter: FavoriteFilter
  /**
   * Rows shown whatever their mark (the current page, a row hidden while the
   * pointer is still on the menu), so nothing vanishes from under the user.
   */
  shownAnyway: ReadonlySet<string>
  /** A search finds hidden rows too: it's how a rarely used one is reached. */
  searching: boolean
}

/** The menu shows the row for the page at `path`. */
export function menuShows(path: string, filters: MenuFilters): boolean {
  const isHidden = filters.hidden.has(path)
  const isFavorite = filters.favorites.has(path)
  if (filters.favoriteFilter === 'favorites' && !isFavorite) return false
  if (filters.favoriteFilter === 'non-favorites' && isFavorite) return false
  if (filters.hiddenFilter === 'hidden') return isHidden
  if (filters.hiddenFilter === 'unhidden' && isHidden) {
    return filters.searching || filters.shownAnyway.has(path)
  }
  return true
}

/**
 * The filters a menu applies: a filter only means something while the menu
 * holds a page it picks out, so with no hidden rows (or no favorites) it goes
 * back to its default, and its header icon isn't shown.
 */
export function effectiveMenuFilters(
  paths: readonly string[],
  hidden: ReadonlySet<string>,
  favorites: ReadonlySet<string>,
  chosen: { hiddenFilter: HiddenFilter; favoriteFilter: FavoriteFilter }
) {
  const hasHidden = paths.some((path) => hidden.has(path))
  const hasFavorites = paths.some((path) => favorites.has(path))
  return {
    hasHidden,
    hasFavorites,
    hiddenFilter: hasHidden ? chosen.hiddenFilter : DEFAULT_HIDDEN_FILTER,
    favoriteFilter: hasFavorites ? chosen.favoriteFilter : DEFAULT_FAVORITE_FILTER,
  }
}

/**
 * Folders with nothing in them show only where the menu shows every kind of
 * row; a menu narrowed to favorites or to hidden rows drops them.
 */
export function showsEmptyFolders(filters: Pick<MenuFilters, 'hiddenFilter' | 'favoriteFilter'>) {
  return filters.hiddenFilter !== 'hidden' && filters.favoriteFilter !== 'favorites'
}
